import { Vec2 } from '../utils/math';

/** 1 pixel = 1 micrometer. Visible light (400-700nm) = 0.4-0.7 px wavelength. */
export const PX_TO_METERS = 1e-6;

export interface Polarization {
  s: number; // fraction of intensity in s-polarization (0-1)
  p: number; // fraction of intensity in p-polarization (0-1)
}

export const UNPOLARIZED: Polarization = { s: 0.5, p: 0.5 };

export interface TracedSegment {
  start: Vec2;
  end: Vec2;
  wavelength: number;   // meters
  intensity: number;    // 0-1
  sourceId: number;     // ID of the laser that emitted this ray chain
  phase: number;        // accumulated phase in radians at segment end
  pathLength: number;   // total optical path length in px from source
  coherent: boolean;    // false if pathLength exceeds coherenceLength
}

export class Ray {
  position: Vec2;
  direction: Vec2;
  wavelength: number;       // meters (e.g., 650e-9 for red)
  intensity: number;        // 0-1
  sourceId: number;         // ID of originating laser
  polarization: Polarization;
  phase: number;            // accumulated phase (radians)
  pathLength: number;       // total path from source (px)
  coherent: boolean;        // still within coherence length?
  coherenceLength: number;  // max coherent path (px), 0 = infinite
  isWhiteLight: boolean;    // from white-light source (for grating subdivision)

  constructor(
    position: Vec2, direction: Vec2, wavelength: number,
    intensity: number = 1, sourceId: number = 0,
    polarization: Polarization = UNPOLARIZED,
    phase: number = 0, pathLength: number = 0,
    coherent: boolean = true, coherenceLength: number = 0,
    isWhiteLight: boolean = false,
  ) {
    this.position = position;
    this.direction = direction.normalize();
    this.wavelength = wavelength;
    this.intensity = intensity;
    this.sourceId = sourceId;
    this.polarization = { ...polarization };
    this.phase = phase;
    this.pathLength = pathLength;
    this.coherent = coherent;
    this.coherenceLength = coherenceLength;
    this.isWhiteLight = isWhiteLight;
  }

  clone(): Ray {
    return new Ray(
      this.position.clone(), this.direction.clone(),
      this.wavelength, this.intensity, this.sourceId,
      { ...this.polarization },
      this.phase, this.pathLength, this.coherent, this.coherenceLength,
      this.isWhiteLight,
    );
  }
}
